import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '@/config';
import type { Organization } from '@/hooks/useUser';

/**
 * Toggles an organization's MSP designation.
 *
 * Sends only `isMsp`: `PATCH /api/organizations/{id}` is a partial update, and
 * echoing `name` back would re-derive the org's `slug` server-side.
 *
 * Invalidates the current-user query because `isMsp` rides on that payload —
 * that is what makes `useIsMsp()` (and therefore the MSP nav) update in place.
 */
export function useSetOrgIsMsp(organizationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<Organization, Error, boolean>({
    mutationFn: async (isMsp) => {
      if (!organizationId) {
        throw new Error('No organization to update');
      }
      const response = await axios.patch<Organization>(
        `${config.backendApi}/api/organizations/${organizationId}`,
        { isMsp },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}
